import * as React from 'react';
import { Link, Outlet, createFileRoute, useMatches } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Eye, Upload } from 'lucide-react';
import { api, qk } from '@client/lib/api';
import { useAdminRoom, useAdminGrants } from '@client/lib/admin-api';
import { PageHeader, RoomStatusBadge } from '@client/components/admin/shared';
import { Button } from '@client/components/ui/button';
import { Dialog } from '@client/components/ui/overlay';
import { Spinner } from '@client/components/ui/feedback';
import { useToast } from '@client/components/ui/toast';
import { cn } from '@client/lib/utils';

export const Route = createFileRoute('/admin/rooms/$id')({ component: RoomHub });

const tabs = [
  { to: '/admin/rooms/$id', label: 'Content', exact: true },
  { to: '/admin/rooms/$id/updates', label: 'Updates' },
  { to: '/admin/rooms/$id/members', label: 'Members' },
  { to: '/admin/rooms/$id/settings', label: 'Settings' },
  { to: '/admin/rooms/$id/share', label: 'Share links' },
  { to: '/admin/rooms/$id/analytics', label: 'Analytics' },
  { to: '/admin/rooms/$id/activity', label: 'Activity' },
] as const;

/** Room hub with section tabs (Content · Members · Settings · Analytics · Activity), the platform's hub-and-tabs pattern. */
function RoomHub() {
  const { id } = Route.useParams();
  const room = useAdminRoom(id);
  const qc = useQueryClient();
  const toast = useToast();
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const grants = useAdminGrants(id);
  const matches = useMatches();
  void matches;
  const status = useMutation({
    mutationFn: (s: string) => api(`/api/admin/rooms/${id}/status`, { body: { status: s } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.admin('room', id) }); qc.invalidateQueries({ queryKey: qk.admin('rooms') }); toast.push({ tone: 'good', title: 'Room status updated' }); },
    onError: (e) => toast.push({ tone: 'critical', title: 'Could not change status', body: String((e as Error).message) }),
  });
  const preview = useMutation({
    mutationFn: (investorId: string) => api<{ url: string }>(`/api/admin/rooms/${id}/preview-as`, { body: { investor_id: investorId } }),
    onSuccess: (res) => { setPreviewOpen(false); window.open(res.url, '_blank', 'noopener'); },
  });
  if (room.isPending) return <Spinner />;
  if (!room.data) return null;
  const r = room.data.room;
  const people = (grants.data?.grants ?? []).flatMap((g) => (g.investorId ? [{ id: g.investorId, name: g.investor_name ?? '', email: g.investor_email ?? '' }] : g.firm_people.map((p) => ({ id: p.id, name: p.name, email: p.email }))));
  const uniquePeople = [...new Map(people.map((p) => [p.id, p])).values()];
  return (
    <>
      <PageHeader
        eyebrow={<Link to="/admin/rooms" className="no-underline text-muted hover:text-foreground">Rooms</Link>}
        title={<span className="flex items-center gap-3">{r.name}<RoomStatusBadge status={r.status} /></span>}
        actions={
          <>
            <Button variant="secondary" onClick={() => setPreviewOpen(true)} disabled={uniquePeople.length === 0}><Eye />Preview as investor</Button>
            {r.status === 'draft' ? <Button onClick={() => status.mutate('open')} loading={status.isPending}><Upload />Open room</Button> : null}
            {r.status === 'open' ? <Button variant="secondary" onClick={() => status.mutate('closed')} loading={status.isPending}>Close room</Button> : null}
            {r.status === 'closed' ? <Button variant="secondary" onClick={() => status.mutate('open')} loading={status.isPending}>Reopen</Button> : null}
          </>
        }
      >
        <nav className="-mb-px flex gap-1 overflow-x-auto" aria-label="Room sections">
          {tabs.map((t) => (
            <Link key={t.to} to={t.to} params={{ id }} activeOptions={{ exact: 'exact' in t && t.exact }} className="relative px-3 py-2.5 text-[13px] font-[600] text-muted no-underline hover:text-foreground after:absolute after:inset-x-3 after:bottom-0 after:h-[2.5px] after:rounded-t after:bg-transparent" activeProps={{ className: cn('text-link hover:text-link after:bg-gradient-brand') }}>
              {t.label}
            </Link>
          ))}
        </nav>
      </PageHeader>
      <Outlet />
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen} title="Preview as investor" description="Opens the room exactly as this person sees it. Preview sessions are excluded from analytics.">
        <div className="max-h-72 divide-y divide-border overflow-y-auto rounded-card border border-border">
          {uniquePeople.map((p) => (
            <button key={p.id} className="flex w-full items-center justify-between px-3 py-2 text-left text-[13px] hover:bg-row-hover" onClick={() => preview.mutate(p.id)}>
              <span className="font-[520]">{p.name}</span><span className="text-muted">{p.email}</span>
            </button>
          ))}
        </div>
      </Dialog>
    </>
  );
}
